/**
 * USE-HELP-ARTICLE.UTILS
 * Reads a help article from Strapi (once per key and per session), the hook answers undefined while it is
 * loading and null if the article does not exist : the help button is only displayed for the articles that are published
 */
import { useEffect, useState } from "react"
import env from "@/env"
import { HelpArticle, getHelpArticleUrl, parseHelpArticle } from "@/utils/help-article.utils"

const cache = new Map<string, Promise<HelpArticle | null>>()

//Plain fetch (not the instances of the application) : no token of the user is sent to Strapi
export function fetchHelpArticle(key: string): Promise<HelpArticle | null> {
  let request = cache.get(key)
  if (!request) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 8000)
    const read = (withSections: boolean) =>
      fetch(getHelpArticleUrl(env.REACT_APP_URL_STRAPI, key, withSections), {
        headers: { Accept: "application/json" },
        credentials: "omit",
        signal: controller.signal
      })
    request = read(true)
      .then((response) => (response.ok ? response : read(false))) //Strapi that does not know the sections yet : without them
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => parseHelpArticle(json))
      .catch(() => {
        cache.delete(key) //Try again next time
        return null
      })
      .finally(() => clearTimeout(timeout))
    cache.set(key, request)
  }
  return request
}

//undefined while loading, null if the article does not exist
export function useHelpArticle(key?: string | null): HelpArticle | null | undefined {
  const [article, setArticle] = useState<HelpArticle | null | undefined>(undefined)

  useEffect(() => {
    let isCancelled = false
    setArticle(undefined)
    if (key) {
      fetchHelpArticle(key).then((result) => {
        if (!isCancelled) setArticle(result)
      })
    }
    return () => {
      isCancelled = true
    }
  }, [key])

  return article
}
